import { Button } from '@heidi/design-system/button';
import { CopyButton } from '@heidi/design-system/copy-button';
import { ThumbsDownFilled, ThumbsUpFilled } from '@heidi/design-system/feedback-thumbs';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { Textarea } from '@heidi/design-system/textarea';
import { toast } from '@heidi/design-system/toast';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyP2Bold } from '@heidi/design-system/typography';
import { logger, sanitizeError } from '@heidi/runtime/logger';
import { cn, useOverlayTriggerTooltip } from '@heidi/utils';
import { ThumbsDown, ThumbsUp, type LucideIcon } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import type { ProjectFeedbackRating } from '../../../contracts/projects/requests.types';
import type { TrackWorkMessageActioned } from '../../../contracts/work-launch-analytics.types';
import { projectMessageActionsMessages } from '../../../data/projects/components/projectMessageActions.messages';
import { getWorkHostPort } from '../../work-home/work-home-ports';

export interface ProjectMessageActionsProps {
  /** Settled assistant prose for the latest turn; copy is hidden when absent. */
  copyableText?: string | null;
  onSubmitFeedback: (rating: ProjectFeedbackRating, comment?: string) => Promise<unknown>;
  className?: string;
  projectId?: string;
  /** Disables the thumbs (not copy — copy needs no project) without hiding
   *  them, e.g. before a provisional chat's project exists: the controls stay
   *  visible, but nothing can fire `onSubmitFeedback` against an empty id. */
  feedbackDisabled?: boolean;
  /** Renders copy alone, e.g. on a closed run where feedback no longer applies. */
  hideFeedback?: boolean;
  /** Rating already given for this reply (e.g. before it stopped being the
   *  latest), so the selected thumb survives the row remounting. */
  initialSelected?: ProjectFeedbackRating | null;
}

/** What the app supplies: the message-actioned emit. The payloads are decided here. */
export interface ProjectMessageActionsHost {
  trackMessageActioned: TrackWorkMessageActioned;
}

const getFeedbackCopy = (
  intl: IntlShape,
  rating: ProjectFeedbackRating
): { label: string; title: string; placeholder: string } => {
  if (rating === 'thumb_up') {
    return {
      label: intl.formatMessage(projectMessageActionsMessages.positiveTooltip),
      title: intl.formatMessage(projectMessageActionsMessages.thumbUpTitle),
      placeholder: intl.formatMessage(projectMessageActionsMessages.thumbUpPlaceholder),
    };
  }
  return {
    label: intl.formatMessage(projectMessageActionsMessages.negativeTooltip),
    title: intl.formatMessage(projectMessageActionsMessages.thumbDownTitle),
    placeholder: intl.formatMessage(projectMessageActionsMessages.thumbDownPlaceholder),
  };
};

export const ProjectMessageActionsView = ({
  copyableText,
  onSubmitFeedback,
  className,
  projectId,
  feedbackDisabled = false,
  hideFeedback = false,
  initialSelected = null,
  trackMessageActioned,
}: ProjectMessageActionsProps & ProjectMessageActionsHost) => {
  const intl = useIntl();
  const [selected, setSelected] = useState<ProjectFeedbackRating | null>(initialSelected);
  const [openFor, setOpenFor] = useState<ProjectFeedbackRating | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const handleThumbClick = (rating: ProjectFeedbackRating) => {
    if (feedbackDisabled) return;
    if (openFor === rating) {
      setOpenFor(null);
      return;
    }
    setComment('');
    setOpenFor(rating);
  };

  const handleSubmit = async (rating: ProjectFeedbackRating) => {
    if (feedbackDisabled || submittingRef.current) return;
    const previous = selected;
    const text = comment.trim();
    submittingRef.current = true;
    setSelected(rating);
    setOpenFor(null);
    setIsSubmitting(true);
    try {
      await onSubmitFeedback(rating, text || undefined);
      trackMessageActioned({
        action: 'feedback_submitted',
        project_id: projectId,
        surface: 'project_chat',
        feedback: rating === 'thumb_up' ? 'positive' : 'negative',
        has_comment: Boolean(text),
      });
      toast({
        tone: 'success',
        title: intl.formatMessage(projectMessageActionsMessages.thanksForFeedback),
      });
    } catch {
      setSelected(previous);
      toast({
        tone: 'destructive',
        title: intl.formatMessage(projectMessageActionsMessages.couldntSubmitFeedback),
        description: intl.formatMessage(projectMessageActionsMessages.pleaseTryAgain),
      });
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleCopy = useCallback(async () => {
    const text = copyableText?.trim();
    // `CopyButton` treats `undefined` as success, so the no-op branch must say false.
    if (!text) return false;
    try {
      await navigator.clipboard.writeText(text);
      trackMessageActioned({
        action: 'copied',
        project_id: projectId,
        surface: 'project_chat',
        message_role: 'assistant',
        copy_length: text.length,
      });
      return true;
    } catch (error) {
      // Never the raw error: a library error's text can carry PHI.
      logger.error('Failed to copy project reply to clipboard', {
        error: sanitizeError(error),
        messageLength: text.length,
      });
      toast({
        tone: 'destructive',
        title: intl.formatMessage(projectMessageActionsMessages.error),
        description: intl.formatMessage(projectMessageActionsMessages.failedToCopy),
      });
      return false;
    }
  }, [copyableText, intl, projectId, trackMessageActioned]);

  const showCopy = Boolean(copyableText?.trim());
  // While a feedback popover is open or submitting, a hover-revealed row stays up.
  const rowEngaged = openFor !== null || isSubmitting;

  if (hideFeedback && !showCopy) return null;

  return (
    <div
      className={cn('flex flex-row items-center justify-between gap-2', className)}
      data-testid="project-message-actions"
    >
      {hideFeedback ? null : (
        <div className="flex items-center gap-0.5">
          <ThumbFeedback
            rating="thumb_up"
            icon={ThumbsUp}
            filledIcon={ThumbsUpFilled}
            isSelected={selected === 'thumb_up'}
            pinned={rowEngaged || selected === 'thumb_up'}
            isOpen={openFor === 'thumb_up'}
            isSubmitting={isSubmitting}
            disabled={feedbackDisabled}
            comment={comment}
            onToggle={() => handleThumbClick('thumb_up')}
            onClose={() => setOpenFor(null)}
            onCommentChange={setComment}
            onSubmit={() => void handleSubmit('thumb_up')}
          />
          <ThumbFeedback
            rating="thumb_down"
            icon={ThumbsDown}
            filledIcon={ThumbsDownFilled}
            isSelected={selected === 'thumb_down'}
            pinned={rowEngaged || selected === 'thumb_down'}
            isOpen={openFor === 'thumb_down'}
            isSubmitting={isSubmitting}
            disabled={feedbackDisabled}
            comment={comment}
            onToggle={() => handleThumbClick('thumb_down')}
            onClose={() => setOpenFor(null)}
            onCommentChange={setComment}
            onSubmit={() => void handleSubmit('thumb_down')}
          />
        </div>
      )}

      {showCopy ? (
        <CopyButton
          onCopy={handleCopy}
          variant="ghost"
          size="md"
          className="text-text-secondary"
          label={intl.formatMessage(projectMessageActionsMessages.copy)}
          copiedLabel={intl.formatMessage(projectMessageActionsMessages.copied)}
          data-testid="project-reply-action-copy"
          data-response-action=""
          data-response-action-pinned={rowEngaged ? '' : undefined}
        />
      ) : null}
    </div>
  );
};

interface ThumbFeedbackProps {
  rating: ProjectFeedbackRating;
  icon: LucideIcon;
  filledIcon: LucideIcon;
  /** The submitted rating; filled so it reads as chosen, not just hovered or open. */
  isSelected: boolean;
  /** Stays visible when the row is hover-revealed. */
  pinned: boolean;
  isOpen: boolean;
  isSubmitting: boolean;
  disabled: boolean;
  comment: string;
  onToggle: () => void;
  onClose: () => void;
  onCommentChange: (value: string) => void;
  onSubmit: () => void;
}

const ThumbFeedback = ({
  rating,
  icon: OutlineIcon,
  filledIcon: FilledIcon,
  isSelected,
  pinned,
  isOpen,
  isSubmitting,
  disabled,
  comment,
  onToggle,
  onClose,
  onCommentChange,
  onSubmit,
}: ThumbFeedbackProps) => {
  const intl = useIntl();
  const { label, title, placeholder } = getFeedbackCopy(intl, rating);
  const { tooltipProps, contentProps } = useOverlayTriggerTooltip();
  const Icon = isSelected ? FilledIcon : OutlineIcon;
  return (
    <Popover
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Tooltip {...tooltipProps}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="md"
              iconOnly
              onClick={onToggle}
              disabled={disabled}
              aria-label={label}
              aria-pressed={isSelected}
              data-response-action=""
              data-response-action-pinned={pinned ? '' : undefined}
              className="text-text-secondary"
            >
              <Icon aria-hidden="true" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
      <PopoverContent {...contentProps} align="start" side="top" className="w-72 p-3">
        <div className="flex flex-col gap-2">
          <TypographyP2Bold className="text-sm text-text-primary">
            {title}
          </TypographyP2Bold>
          <Textarea
            autoFocus
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            placeholder={placeholder}
            className="h-20 resize-none"
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {intl.formatMessage(projectMessageActionsMessages.cancel)}
            </Button>
            <Button type="button" size="xs" onClick={onSubmit} disabled={isSubmitting}>
              {isSubmitting
                ? intl.formatMessage(projectMessageActionsMessages.submitting)
                : intl.formatMessage(projectMessageActionsMessages.submit)}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const trackMessageActioned: TrackWorkMessageActioned = (props) => {
  const shell = getWorkHostPort('shell');
  shell.trackWorkEvent(shell.workLaunchEventName.WorkMessageActioned, props);
};

/** Binds a Work reply's action row to the Work analytics emit. */
export const ProjectMessageActions = ({
  hideFeedback = false,
  ...props
}: ProjectMessageActionsProps) => (
  <ProjectMessageActionsView
    {...props}
    hideFeedback={hideFeedback || !getWorkHostPort('shell').isProductFeedbackAvailable}
    trackMessageActioned={trackMessageActioned}
  />
);
